import { html, isServer, LitElement } from 'lit'
import { customElement, property, state } from 'lit/decorators.js'
import { observe } from '../common/decorators/observe.js'
import { NordEvent } from '../common/events.js'
import { clamp } from '../common/number.js'
import { storage } from '../common/storage.js'

import componentStyle from '../common/styles/Component.css'
import style from './AsideDrawer.css'

export type AsideDrawerWidthSource = 'drag' | 'keyboard' | 'attribute' | 'storage'

/**
 * Event dispatched when the drawer's width changes. The `width` property is
 * the new applied inline-size in px, and `source` is the cause of the change.
 */
export class AsideDrawerWidthChangeEvent extends NordEvent {
  declare width: number
  declare source: AsideDrawerWidthSource

  constructor(width: number, source: AsideDrawerWidthSource) {
    super('width-change')
    Object.defineProperty(this, 'width', {
      value: width,
      enumerable: true,
      writable: false,
    })
    Object.defineProperty(this, 'source', {
      value: source,
      enumerable: true,
      writable: false,
    })
  }
}

const STORAGE_KEY_PREFIX = 'nord-aside.'

function widthKey(id: string) {
  return `${STORAGE_KEY_PREFIX}${id}.width`
}

function defaultWidthKey(id: string) {
  return `${STORAGE_KEY_PREFIX}${id}.defaultWidth`
}

/**
 * A drawer panel inside `<nord-aside>`.
 *
 * @status new
 * @category layout
 * @slot - Default slot. Place arbitrary drawer content here.
 *
 * @fires {AsideDrawerWidthChangeEvent} width-change - Dispatched when the
 * drawer's width changes. The event exposes `width` (the new applied
 * inline-size in px) and `source` (`'drag' | 'keyboard' | 'attribute' | 'storage'`).
 *
 * @parent Aside
 */
@customElement('nord-aside-drawer')
export default class AsideDrawer extends LitElement {
  static styles = [componentStyle, style]

  /**
   * Initial width in px. The persisted value overrides this on load; if
   * `default-width` changes between sessions, the persisted value is
   * re-seeded to the new default.
   */
  @property({ reflect: true, type: Number, attribute: 'default-width' }) defaultWidth: number = 420

  /**
   * Resize clamp lower bound in px.
   */
  @property({ reflect: true, type: Number, attribute: 'min-width' }) minWidth: number = 280

  /**
   * Resize clamp upper bound in px. Defaults to `Number.MAX_SAFE_INTEGER`
   * so an unset `max-width` doesn't constrain the drawer — the consumer's
   * layout (or the rail / nav anchoring) determines the practical ceiling.
   * Set an explicit value to clamp.
   *
   * Not reflected: an unset cap is the `Number.MAX_SAFE_INTEGER` sentinel,
   * which must never render into the DOM as `max-width="9007199254740991"`.
   */
  @property({ reflect: false, type: Number, attribute: 'max-width' }) maxWidth: number = Number.MAX_SAFE_INTEGER

  /**
   * Maximum inline-size in px the drawer can reach when `[fullscreen]`
   * is active. Independent of `max-width` (which only caps the docked
   * drag / keyboard resize) — fullscreen typically wants to grow
   * larger than the docked maximum. Defaults to `Number.MAX_SAFE_INTEGER`
   * so an unset value lets fullscreen fill all available room (bounded
   * by the layout's left-cap rule in `<nord-aside>`).
   *
   * Not reflected: an unset cap is the `Number.MAX_SAFE_INTEGER` sentinel,
   * which must never render into the DOM.
   */
  @property({ reflect: false, type: Number, attribute: 'max-fullscreen-width' }) maxFullscreenWidth: number = Number.MAX_SAFE_INTEGER

  /**
   * When set, the drawer cannot be opened. Programmatic opens are silently
   * ignored; if the drawer is already open and this flips on, the drawer
   * stays open until closed normally.
   */
  @property({ type: Boolean, reflect: true }) disabled: boolean = false

  /**
   * Reflected. Phase 3 wires the overlay sizing internally.
   */
  @property({ type: Boolean, reflect: true }) fullscreen: boolean = false

  /**
   * Set by the parent `<nord-aside>` when the drawer renders as a narrow
   * floating overlay (rather than a docked region). When `true` the host
   * becomes an accessible modal dialog (`role="dialog"` + `aria-modal`);
   * when `false` the host is a `role="region"` landmark. Drive this only
   * through the parent — it owns the floating-vs-docked decision.
   */
  @property({ type: Boolean, reflect: false }) floating: boolean = false

  /**
   * Currently-applied width in px. Public read-only-ish; use `setWidth()`
   * (or drive via `default-width` + min/max attrs) rather than poking
   * this directly.
   * @internal
   */
  @state() width = 420

  private widthSeeded = false
  private fullscreenSeeded = false
  private fullscreenAnimationCleanup?: () => void
  private fullscreenAnimation?: Animation
  // Last painted inline-size, kept up to date by ResizeObserver. Used as
  // the animation's `fromWidth` on toggle — read from this cache instead
  // of `getBoundingClientRect()` (which would force sync layout against
  // the already-mutated `fullscreen` attribute).
  private observedWidth = 0
  private fullscreenResizeObserver?: ResizeObserver

  render() {
    return html`
      <div class="n-aside-drawer" part="drawer">
        <slot></slot>
      </div>
    `
  }

  connectedCallback() {
    super.connectedCallback()
    this.applyFloatingRole()
    this.applyAriaLabelledby()
    if (isServer) {
      return
    }
    // Seed observedWidth synchronously so the first toggle has a valid
    // fromWidth even before ResizeObserver has fired.
    this.observedWidth = this.getBoundingClientRect().width
    if (typeof ResizeObserver !== 'undefined') {
      this.fullscreenResizeObserver = new ResizeObserver((entries) => {
        // Don't overwrite while an animation is running — intermediate
        // frames aren't valid `fromWidth` candidates for the NEXT toggle.
        if (this.hasAttribute('data-animating-fullscreen'))
          return
        const entry = entries[0]
        if (!entry)
          return
        const size = entry.borderBoxSize?.[0]?.inlineSize ?? entry.contentRect.width
        this.observedWidth = size
      })
      this.fullscreenResizeObserver.observe(this)
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback()
    this.fullscreenResizeObserver?.disconnect()
    this.fullscreenResizeObserver = undefined
    this.fullscreenAnimation?.cancel()
    this.fullscreenAnimation = undefined
    this.fullscreenAnimationCleanup = undefined
    // The cancel EVENT is queued, not synchronous, and its cleanup no-ops
    // once `fullscreenAnimation` is cleared — so remove the animation
    // state directly. Without this, a drawer disconnected mid-toggle
    // (or while the tab is hidden and rAFs are paused) reconnects with a
    // stale width pin and a stuck `position: fixed`.
    this.removeAttribute('data-animating-fullscreen')
    this.style.removeProperty('inline-size')
    this.style.removeProperty('max-inline-size')
    this.style.removeProperty('min-inline-size')
  }

  willUpdate() {
    if (!this.widthSeeded) {
      this.seedWidth()
      this.widthSeeded = true
    }
  }

  /**
   * Animate the fullscreen toggle ONLY when the user flips the attribute.
   * Not on initial mount, not on drag-resize, not on show/hide, not on
   * nav-state-driven size changes.
   *
   * Uses Web Animations API with explicit `from`/`to` keyframes because
   * the drawer's `position` flips between `relative` (docked, in flex
   * flow) and `fixed` (fullscreen, overlay). CSS transitions don't
   * reliably interpolate `inline-size` across a position-type change.
   *
   * `fromWidth` comes from `observedWidth` — the last size painted by
   * the browser, captured by ResizeObserver BEFORE the consumer flipped
   * the attribute. Reading `getBoundingClientRect()` here would return
   * the post-toggle size (Lit has already reflected the attribute), so
   * the cached value is the only reliable pre-toggle width.
   *
   * `toWidth` is measured inside rAF, after Lit has committed the new
   * attribute and Aside has updated `--_n-aside-drawer-fullscreen-size`.
   *
   * Exiting also resets the drawer's docked width to its declared
   * `default-width` so the user lands at a predictable size after
   * collapsing.
   */
  @observe('fullscreen')
  protected handleFullscreenChange(_oldValue: boolean | undefined, newValue: boolean) {
    if (isServer) {
      return
    }
    if (!this.fullscreenSeeded) {
      this.fullscreenSeeded = true
      this.observedWidth = this.getBoundingClientRect().width
      return
    }
    // At narrow viewports the `fullscreen` attribute has no visual effect
    // (the parent <nord-aside> renders the drawer as a floating overlay
    // regardless). The CSS rules for [fullscreen] are gated behind
    // `@media (min-width: 768px)` — skip the animation here too so we
    // don't run a no-op WAAPI animation against zero size delta.
    if (typeof matchMedia !== 'undefined' && !matchMedia('(min-width: 768px)').matches) {
      return
    }

    // Reverse-mid-flight: snapshot the current visible width BEFORE
    // cancelling, because `animation.cancel()` synchronously reverts
    // the element to its CSS layout — measuring after cancel would
    // give us the underlying CSS width, not what the user is seeing.
    // The cancel EVENT however is queued, firing in the next rendering
    // update — AFTER this handler has re-set `data-animating-fullscreen`
    // below. Clear `fullscreenAnimation` here so the stale animation's
    // cleanup fails its identity guard and can't strip the attribute
    // this new toggle now owns (which would drop `position: fixed`
    // mid-animation and push the drawer back into flex flow).
    if (this.fullscreenAnimation) {
      this.observedWidth = this.getBoundingClientRect().width
      this.fullscreenAnimation.cancel()
      this.fullscreenAnimation = undefined
      this.fullscreenAnimationCleanup = undefined
    }

    const fromWidth = this.observedWidth

    // Pin `position: fixed` for the entire animation in both directions
    // via `:host([data-animating-fullscreen])`. Setting this here also
    // tells the ResizeObserver to stop updating `observedWidth`.
    this.setAttribute('data-animating-fullscreen', '')

    if (!newValue) {
      this.setWidth(this.defaultWidth, 'attribute')
    }

    // Pin the pre-toggle width inline for the frame(s) between the
    // attribute flip and the animation start below — without this the
    // browser paints the CSS end-state (fullscreen or re-docked) for a
    // frame, flashing the final size before the animation snaps back to
    // `fromWidth`. The pin is removed just before measuring the animation
    // target; once running, the animation's keyframes outrank inline
    // styles anyway.
    this.style.setProperty('inline-size', `${fromWidth}px`)
    this.style.setProperty('max-inline-size', `${fromWidth}px`)
    this.style.setProperty('min-inline-size', `${fromWidth}px`)

    // Double-rAF: the first frame lets Aside's scheduleLeftCapReconcile rAF
    // (queued synchronously above via syncFullscreenAttr) write
    // `--_n-aside-drawer-fullscreen-available`. The second frame then reads
    // the post-reconcile toWidth so the animation end-state is correct.
    requestAnimationFrame(() => requestAnimationFrame(() => {
      this.style.removeProperty('inline-size')
      this.style.removeProperty('max-inline-size')
      this.style.removeProperty('min-inline-size')

      const toWidth = this.getBoundingClientRect().width

      if (Math.abs(fromWidth - toWidth) < 1) {
        this.removeAttribute('data-animating-fullscreen')
        this.observedWidth = toWidth
        this.fullscreenAnimationCleanup = undefined
        return
      }

      const animation = this.animate(
        [
          {
            inlineSize: `${fromWidth}px`,
            maxInlineSize: `${fromWidth}px`,
            minInlineSize: `${fromWidth}px`,
          },
          {
            inlineSize: `${toWidth}px`,
            maxInlineSize: `${toWidth}px`,
            minInlineSize: `${toWidth}px`,
          },
        ],
        {
          duration: 300,
          easing: 'cubic-bezier(0.4, 0, 0.2, 1)',
          fill: 'none',
        },
      )

      this.fullscreenAnimation = animation

      const cleanup = () => {
        if (this.fullscreenAnimation !== animation)
          return
        this.fullscreenAnimation = undefined
        this.fullscreenAnimationCleanup = undefined
        this.removeAttribute('data-animating-fullscreen')
        this.observedWidth = this.getBoundingClientRect().width
      }
      this.fullscreenAnimationCleanup = cleanup
      animation.onfinish = cleanup
      animation.oncancel = cleanup
    }))
  }

  @observe('defaultWidth')
  @observe('minWidth')
  @observe('maxWidth')
  @observe('maxFullscreenWidth')
  protected handleBoundsChange() {
    // Re-seeding happens once on willUpdate; subsequent bound changes
    // just re-clamp the current width.
    if (!this.widthSeeded) {
      return
    }
    const clamped = clamp(this.width, this.minWidth, this.maxWidth)
    if (clamped !== this.width) {
      this.setWidth(clamped, 'attribute')
    }
    else {
      this.applyWidthVars()
    }
  }

  @observe('floating')
  protected handleFloatingChange() {
    this.applyFloatingRole()
  }

  /**
   * Single writer for the host's landmark/dialog role. A floating overlay
   * is an accessible modal (`role="dialog"` + `aria-modal="true"`); a docked
   * drawer is a `role="region"` landmark with no aria-modal.
   */
  private applyFloatingRole() {
    if (this.floating) {
      this.setAttribute('role', 'dialog')
      this.setAttribute('aria-modal', 'true')
    }
    else {
      this.setAttribute('role', 'region')
      this.removeAttribute('aria-modal')
    }
  }

  @observe('width')
  protected applyWidthVars() {
    this.style.setProperty('--_n-aside-drawer-inline-size', `${this.width}px`)
    this.style.setProperty('--_n-aside-drawer-min-inline-size', `${this.minWidth}px`)
    // An unset `max-width` defaults to MAX_SAFE_INTEGER — too large for
    // CSS to handle reliably. Translate to `none` so the drawer has no
    // CSS-side cap; the clamp in `setWidth` still runs against the
    // numeric value so drag/keyboard don't escape JS-side bounds.
    const isMaxUnbounded = this.maxWidth >= Number.MAX_SAFE_INTEGER
    this.style.setProperty('--_n-aside-drawer-max-inline-size', isMaxUnbounded ? 'none' : `${this.maxWidth}px`)
    // Fullscreen rendered inline-size is the CSS-time `min()` of two vars:
    // - `--_n-aside-drawer-fullscreen-max` (this var, drawer-owned)
    // - `--_n-aside-drawer-fullscreen-available` (written by <nord-aside>
    //   to the layout's left-cap; absent when there's no aside parent)
    // The drawer's own var floors at `minWidth` so a tiny consumer value
    // can never drop the drawer below its declared minimum. Unset
    // `max-fullscreen-width` resolves to `100vw` — a sensible standalone
    // fallback when there's no aside to publish the available width.
    const isFullscreenUnbounded = this.maxFullscreenWidth >= Number.MAX_SAFE_INTEGER
    this.style.setProperty(
      '--_n-aside-drawer-fullscreen-max',
      isFullscreenUnbounded
        ? '100vw'
        : `max(${this.minWidth}px, ${this.maxFullscreenWidth}px)`,
    )
  }

  /**
   * Update the drawer width, clamped to `[min-width, max-width]`. Persists
   * to localStorage and dispatches `width-change` with the given source.
   */
  setWidth(width: number, source: AsideDrawerWidthSource) {
    const next = clamp(Math.round(width), this.minWidth, this.maxWidth)
    if (next === this.width) {
      return
    }
    this.width = next
    if (!isServer && this.id) {
      storage(widthKey(this.id), this.defaultWidth).value = this.width
    }
    this.dispatchEvent(new AsideDrawerWidthChangeEvent(this.width, source))
  }

  /**
   * Whether the drawer is pinned — `min === max === default`. A pinned
   * drawer's resize handle is a drag no-op.
   */
  get isPinned(): boolean {
    return this.minWidth === this.maxWidth
  }

  /**
   * Lift Layout.ts's re-seed pattern: if the consumer-provided
   * `default-width` differs from the last default we wrote for this id,
   * the persisted width is reset to the new default.
   */
  private seedWidth() {
    // On the server there's no localStorage and no layout to paint; clamp
    // the declared default and skip both the storage read/write and the
    // `width-change` dispatch.
    if (isServer || !this.id) {
      this.width = clamp(this.defaultWidth, this.minWidth, this.maxWidth)
      this.applyWidthVars()
      return
    }
    const widthStore = storage(widthKey(this.id), this.defaultWidth)
    const defaultStore = storage(defaultWidthKey(this.id), this.defaultWidth)
    let next: number
    let source: AsideDrawerWidthSource
    if (defaultStore.value !== this.defaultWidth) {
      next = this.defaultWidth
      widthStore.value = next
      defaultStore.value = this.defaultWidth
      source = 'attribute'
    }
    else {
      next = widthStore.value
      source = 'storage'
    }
    this.width = clamp(Math.round(next), this.minWidth, this.maxWidth)
    this.applyWidthVars()
    this.dispatchEvent(new AsideDrawerWidthChangeEvent(this.width, source))
  }

  /**
   * Resolve the matching `<nord-aside-trigger drawer="<this.id>">` inside
   * the ancestor `<nord-aside>` and use it as the drawer's aria-labelledby
   * target. Falls back to no label when no matching trigger exists.
   */
  private applyAriaLabelledby() {
    if (!this.id) {
      this.removeAttribute('aria-labelledby')
      return
    }
    const aside = this.closest('nord-aside')
    if (!aside) {
      return
    }
    const trigger = aside.querySelector<HTMLElement>(
      `nord-aside-trigger[drawer="${this.id}"]`,
    )
    if (!trigger) {
      return
    }
    if (!trigger.id) {
      trigger.id = `${this.id}-trigger`
    }
    this.setAttribute('aria-labelledby', trigger.id)
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'nord-aside-drawer': AsideDrawer
  }
}
